import { Button } from '@helix-ds/react'
import React, { useEffect, useRef } from 'react'

export type VideoDialogProps = {
  isOpen: boolean
  videoId: string
  title: string
  src: string
  closeLabel: string
  backLabel: string
  onClose: () => void
}

const VideoDialog: React.FC<VideoDialogProps> = ({ isOpen, videoId, title, src, closeLabel, backLabel, onClose }) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const titleId = `video-dialog-${videoId}-title`

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog || !isOpen) return

    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousBodyOverflow = document.body.style.overflow

    if (!dialog.open) {
      dialog.showModal()
    }
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousBodyOverflow
      if (dialog.open) {
        dialog.close()
      }
      returnFocusRef.current?.focus()
    }
  }, [isOpen])

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      data-video-id={videoId}
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      className="text-text-heading h-dvh max-h-none w-full max-w-none border-0 bg-transparent p-0 backdrop:bg-black/60 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:w-[min(960px,calc(100vw-2rem))] sm:rounded-xl"
    >
      <div className="bg-background-primary flex h-full w-full flex-col overflow-hidden shadow-2xl sm:h-auto sm:rounded-xl">
        <header className="border-border-base relative grid shrink-0 grid-cols-[auto_1fr] items-center gap-3 border-b px-4 py-3 sm:block sm:px-6 sm:py-4">
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={onClose}
            aria-label={backLabel}
            className="sm:hidden"
          >
            <span aria-hidden="true">&larr;</span>
            <span>{backLabel}</span>
          </Button>

          <h2
            id={titleId}
            className="text-text-heading truncate text-center text-lg font-semibold sm:pr-12 sm:text-left sm:text-xl"
          >
            {title}
          </h2>

          <div className="absolute top-1/2 right-4 hidden -translate-y-1/2 sm:right-6 sm:block">
            <Button type="button" variant="ghost" size="sm" onClick={onClose} aria-label={closeLabel}>
              <span aria-hidden="true" className="text-2xl leading-none">
                &times;
              </span>
            </Button>
          </div>
        </header>

        <div className="sm:bg-background-primary flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-black sm:p-6">
          <div className="aspect-video max-h-full w-full max-w-4xl overflow-hidden bg-black sm:rounded-lg">
            <iframe
              key={`${videoId}:${src}`}
              src={src}
              title={title}
              loading="lazy"
              allow="encrypted-media; fullscreen; picture-in-picture; screen-wake-lock;"
              allowFullScreen
              tabIndex={0}
              className="h-full w-full border-0"
            />
          </div>
        </div>
      </div>
    </dialog>
  )
}

export default VideoDialog
